import { useMemo, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { useCashFlowQuery } from '@/client/api/business/spend';
import { AccountingPageShell } from './components/AccountingPageShell';
import { filterClass } from './components/spend-ui';
import { ReportBody, shiftMonth, thisMonth } from './components/report-ui';
import { EmptyState } from '../HumanResource/components/hr-ui';
import { money } from '../HumanResource/components/comp-format';

/** Cash flow: money in and out by the date it actually moved. */
export default function CashFlowPage() {
  const [span, setSpan] = useState(12);
  const to = thisMonth();
  const from = shiftMonth(to, -(span - 1));
  const q = useCashFlowQuery(from, to);
  const currencies = useMemo(() => [...new Set((q.data?.rows ?? []).filter((r) => r.totals.inflow || r.totals.outflow).map((r) => r.currency))].sort(), [q.data]);
  const [picked, setPicked] = useState<string | null>(null);
  const currency = picked && currencies.includes(picked) ? picked : currencies[0] ?? 'GHS';
  const t = q.data?.totals.find((x) => x.currency === currency);

  return (
    <AccountingPageShell
      mock={false}
      title="Cash flow"
      description="Money in (platform payments, commission, other income) and out (refunds, bills paid, claims reimbursed, payroll paid) by the date it moved. Bills and payroll only count once marked paid."
      crumbs={[{ label: 'Accounting', href: '/admin/accounting' }, { label: 'Reports' }, { label: 'Cash flow' }]}
      icon={ArrowLeftRight}
      actions={
        <div className="flex gap-2">
          <select value={span} onChange={(e) => setSpan(Number(e.target.value))} className={filterClass} aria-label="Range">
            {[3, 6, 12].map((n) => (
              <option key={n} value={n}>
                Last {n} months
              </option>
            ))}
          </select>
          {currencies.length > 1 && (
            <select value={currency} onChange={(e) => setPicked(e.target.value)} className={filterClass} aria-label="Currency">
              {currencies.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          )}
        </div>
      }
      stats={[
        { key: 'in', label: 'Money in', value: t ? money(t.inflow, currency) : '—' },
        { key: 'out', label: 'Money out', value: t ? money(t.outflow, currency) : '—' },
        { key: 'net', label: 'Net cash flow', value: t ? money(t.net, currency) : '—' },
      ]}
    >
      {q.isError ? (
        <EmptyState label={`Could not load the report: ${(q.error as Error)?.message ?? 'unknown error'}`} />
      ) : q.isLoading || !q.data ? (
        <EmptyState label="Working out cash flow…" />
      ) : (
        <ReportBody report={q.data} a="inflow" b="outflow" labels={['Money in', 'Money out']} currency={currency} />
      )}
    </AccountingPageShell>
  );
}
